/* eslint-disable unicorn/no-null */
import type { ChangeEvent, FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';

import {
    Autocomplete,
    Button,
    Switch,
    Select,
    UnstyledButton
} from '@mantine/core';

import validationSchema from './ReleaseVersionForm.schema';
import {
    useV3RegistriesQuery,
    useV3RegistryModulesQuery,
    useCreateModuleMutation
} from './RegistryModules.queries';

interface ReleaseVersionFormProps {
    projectVersionId: string;
    onClose?: () => void;
}

const ReleaseVersionForm: FC<ReleaseVersionFormProps> = ({
    projectVersionId,
    onClose
}) => {
    const [selectedRegistryId, setSelectedRegistryId] = useState<
        string | undefined
    >();
    const [registryError, setRegistryError] = useState<string | undefined>();

    const { data: registries = [] } = useV3RegistriesQuery();
    const { data: items = [] } = useV3RegistryModulesQuery(selectedRegistryId);
    const createModuleMutation = useCreateModuleMutation();

    const initialValues = {
        idn: '',
        version: '',
        project_version_id: projectVersionId,
        is_public: true
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        async onSubmit(formData) {
            if (!selectedRegistryId) {
                setRegistryError('Registry is required');
                return;
            }

            if (createModuleMutation.isPending) {
                return;
            }
            try {
                await createModuleMutation.mutateAsync({
                    registryId: selectedRegistryId,
                    data: {
                        project_version_id: formData.project_version_id,
                        idn: formData.idn,
                        version: formData.version,
                        title: formData.idn,
                        description: '',
                        is_public: formData.is_public
                    }
                });
                onClose?.();
            } catch {
                return;
            }
        }
    });

    const moduleVersions = useMemo(
        () => items.filter((item) => item.idn === formik.values.idn),
        [formik.values.idn, items]
    );

    useEffect(() => {
        if (registries.length > 0) {
            const productionRegistryId = registries.find(
                (registry) => registry.idn === 'production'
            )?.id;
            setSelectedRegistryId(productionRegistryId);
        }
    }, [registries]);

    useEffect(() => {
        if (selectedRegistryId) {
            void formik.setFieldValue('idn', '');
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRegistryId]);

    const handleCancel = () => {
        onClose?.();
    };

    const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
        void formik.setFieldValue('is_public', event.target.checked);
    };

    const moduleOptions = useMemo(
        () => Array.from(new Set(items.map((item) => item.idn))),
        [items]
    );

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <div>Release Project Version</div>
            <Select
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={registries.map((registry) => ({
                    label: registry.idn,
                    value: registry.id
                }))}
                error={registryError}
                label="Registry"
                name="registry_id"
                placeholder="Select Registry"
                value={selectedRegistryId ?? null}
                onChange={(value) => {
                    setSelectedRegistryId(value ?? undefined);
                    setRegistryError(undefined);
                }}
            />

            <Autocomplete
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={moduleOptions}
                error={formik.touched.idn ? formik.errors.idn : undefined}
                label="Module"
                name="idn"
                placeholder="Select module"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={(value) => {
                    void formik.setFieldValue('idn', value);
                }}
            />

            <Autocomplete
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={moduleVersions.map((v) => ({
                    label: v.version,
                    value: v.version
                }))}
                disabled={!selectedRegistryId}
                error={
                    formik.touched.version ? formik.errors.version : undefined
                }
                label="Release Version"
                name="version"
                placeholder="1.0.0"
                value={formik.values.version}
                onBlur={formik.handleBlur}
                onChange={(value) => {
                    void formik.setFieldValue('version', value);
                }}
            />

            <Switch
                checked={formik.values.is_public}
                disabled={!selectedRegistryId}
                label="Deploy"
                name="is_public"
                onChange={handleSwitchChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel Release
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty ||
                        !formik.isValid ||
                        formik.isSubmitting ||
                        createModuleMutation.isPending
                    }
                    loading={createModuleMutation.isPending}
                    size="xs"
                    type="submit"
                >
                    {formik.values.is_public ? 'Release and Deploy' : 'Release'}
                </Button>
            </div>
        </form>
    );
};

export default ReleaseVersionForm;
